/**
 * Clevertap2WayApptroveNativeSyncPage — CRUD list for CleverTap two-way native
 * sync configurations. Header + create form + table of configured syncs with a
 * pause/resume action.
 */
import { useState, type FormEvent } from "react";
import {
	useNativeSyncList,
	useNativeSyncCreate,
	useNativeSyncPatch,
} from "./hooks";
import type { SyncDirection } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const SELECT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

export function Clevertap2WayApptroveNativeSyncPage() {
	const listQuery = useNativeSyncList();
	const createMut = useNativeSyncCreate();
	const patchMut = useNativeSyncPatch();

	const [syncId, setSyncId] = useState("");
	const [direction, setDirection] = useState<SyncDirection>("bidirectional");
	const [clevertapAccountId, setClevertapAccountId] = useState("");
	const [clevertapPasscode, setClevertapPasscode] = useState("");
	const [appToken, setAppToken] = useState("");

	const submitDisabled =
 createMut.isPending ||
 !syncId.trim() ||
 !clevertapAccountId.trim() ||
 !clevertapPasscode.trim() ||
 !appToken.trim();

	const handleCreate = (e: FormEvent) => {
 e.preventDefault();
 if (submitDisabled) return;
 createMut.mutate(
 {
 sync_id: syncId.trim(),
 direction,
 clevertap_account_id: clevertapAccountId.trim(),
 clevertap_passcode: clevertapPasscode.trim(),
 apptrove_app_token: appToken.trim(),
 },
 {
 onSuccess: () => {
 setSyncId("");
 setClevertapAccountId("");
 setClevertapPasscode("");
 setAppToken("");
 setDirection("bidirectional");
 },
 },
 );
	};

	const items = listQuery.data?.items ?? [];

	return (
 <main
 data-testid="clevertap-2-way-apptrove-native-sync-page"
 className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
 CleverTap native sync
 </p>
 <h1 className="text-green-700 font-semibold text-3xl font-semibold">
 Two-way native sync
 </h1>
 <p className="max-w-2xl text-sm text-zinc-600">
 Configure bi-directional native sync pipelines between your CleverTap
 workspace and Trackerity. Events stream in the chosen direction with
 cursor checkpoints so a restart never replays or drops a batch.
 </p>
 </header>

 <section className={CARD} aria-label="Create native sync">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">New native sync</h2>
 <p className="mt-1 text-xs text-zinc-500">
 Register a CleverTap account plus a native app token, pick a direction,
 and give the pipeline a stable sync id for idempotency.
 </p>
 <form
 onSubmit={handleCreate}
 className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2"
 data-testid="clevertap-2-way-apptrove-native-sync-create-form"
 >
 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
 Sync id
 <input
 type="text"
 className={INPUT}
 placeholder="prod-events-v1"
 value={syncId}
 onChange={(e) => setSyncId(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="clevertap-2-way-apptrove-native-sync-sync-id-input"
 />
 </label>
 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
 Direction
 <select
 className={SELECT}
 value={direction}
 onChange={(e) => setDirection(e.currentTarget.value as SyncDirection)}
 disabled={createMut.isPending}
 data-testid="clevertap-2-way-apptrove-native-sync-direction-input"
 >
 <option value="bidirectional">Bidirectional</option>
 <option value="inbound">Inbound</option>
 <option value="outbound">Outbound</option>
 </select>
 </label>
 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
 CleverTap account id
 <input
 type="text"
 className={INPUT}
 placeholder="ABC-DEF-123"
 value={clevertapAccountId}
 onChange={(e) => setClevertapAccountId(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="clevertap-2-way-apptrove-native-sync-account-input"
 />
 </label>
 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
 CleverTap passcode
 <input
 type="password"
 className={INPUT}
 placeholder="redacted"
 value={clevertapPasscode}
 onChange={(e) => setClevertapPasscode(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="clevertap-2-way-apptrove-native-sync-passcode-input"
 />
 </label>
 <label className="flex flex-col gap-1 text-xs font-medium text-zinc-700 sm:col-span-2">
 Native app token
 <input
 type="password"
 className={INPUT}
 placeholder="redacted"
 value={appToken}
 onChange={(e) => setAppToken(e.currentTarget.value)}
 disabled={createMut.isPending}
 data-testid="clevertap-2-way-apptrove-native-sync-token-input"
 />
 </label>
 <div className="sm:col-span-2">
 <button
 type="submit"
 className={BTN}
 disabled={submitDisabled}
 data-testid="clevertap-2-way-apptrove-native-sync-submit"
 >
 {createMut.isPending ? "Saving." : "Create native sync"}
 </button>
 </div>
 </form>
 {createMut.error ? (
 <p
 className="mt-3 text-xs text-red-600"
 role="alert"
 data-testid="clevertap-2-way-apptrove-native-sync-create-error"
 >
 {createMut.error.message}
 </p>
 ) : null}
 </section>

 <section className={CARD} aria-label="Native sync list">
 <div className="flex items-center justify-between">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">Configured syncs</h2>
 <span className="text-xs text-zinc-500">{items.length} total</span>
 </div>
 {listQuery.isLoading ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="clevertap-2-way-apptrove-native-sync-loading"
 >
 Loading.
 </p>
 ) : listQuery.error ? (
 <p
 className="mt-3 text-sm text-red-600"
 role="alert"
 data-testid="clevertap-2-way-apptrove-native-sync-list-error"
 >
 {listQuery.error.message}
 </p>
 ) : items.length === 0 ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="clevertap-2-way-apptrove-native-sync-empty"
 >
 No native sync configured yet. Create one above to start streaming
 events between CleverTap and Trackerity.
 </p>
 ) : (
 <ul
 className="mt-3 divide-y divide-zinc-200"
 data-testid="clevertap-2-way-apptrove-native-sync-list"
 >
 {items.map((row) => {
 const nextStatus = row.status === "active" ? "paused" : "active";
 return (
 <li
 key={row.id}
 className="flex flex-col gap-2 py-3 text-sm sm:flex-row sm:items-center sm:justify-between"
 data-testid={`clevertap-2-way-apptrove-native-sync-row-${row.id}`}
 >
 <div className="flex flex-col gap-1">
 <span className="font-medium text-zinc-900">{row.sync_id}</span>
 <span className="text-xs text-zinc-500">
 {row.direction} &middot; {row.events_synced.toLocaleString()} events
 </span>
 </div>
 <div className="flex items-center gap-3">
 <span
 className={
 row.status === "active"
 ? "rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"
 : "rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600"
 }
 data-testid={`clevertap-2-way-apptrove-native-sync-status-${row.id}`}
 >
 {row.status}
 </span>
 <button
 type="button"
 className={BTN_GHOST}
 onClick={() =>
 patchMut.mutate({
 id: row.id,
 body: { status: nextStatus },
 })
 }
 disabled={patchMut.isPending}
 data-testid={`clevertap-2-way-apptrove-native-sync-toggle-${row.id}`}
 >
 {row.status === "active" ? "Pause" : "Resume"}
 </button>
 </div>
 </li>
 );
 })}
 </ul>
 )}
 </section>
 </main>
	);
}

export default Clevertap2WayApptroveNativeSyncPage;
